import { CourseResponseDto, CourseFilterKey } from '@repo/types';
import React, { useState, useEffect } from 'react';
import { HiX } from 'react-icons/hi';
import { Badge } from './Badge';
import { Button } from './Button';
import { Input } from './Input';
import { LoadingSpinner } from './LoadingSpinner';
import { useCourses } from '../../hooks/useCourses';

interface CourseSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (course: CourseResponseDto) => void;
  selectedCourseIds?: number[]; // 이미 선택된 강의 ID 목록 (중복 선택 방지용)
}

const CourseSelectModal: React.FC<CourseSelectModalProps> = ({ isOpen, onClose, onSelect, selectedCourseIds = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const limit = 5;

  // 모달이 열릴 때 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setPage(1);
    }
  }, [isOpen]);

  const { data, isLoading, isFetching } = useCourses({
    page,
    limit,
    ...(searchQuery && {
      filterKey: CourseFilterKey.SEARCH,
      filterValue: searchQuery,
    }),
  });

  const handleSelect = (course: CourseResponseDto) => {
    onSelect(course);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white p-4">
          <h2 className="text-xl font-semibold text-slate-800">강의 선택</h2>
          <button onClick={onClose} className="rounded-lg p-1 transition-colors hover:bg-slate-100">
            <HiX className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* 내용 */}
        <div className="p-6">
          <div className="space-y-4">
            {/* 검색창 */}
            <div className="flex gap-2">
              <Input
                id="course-search"
                placeholder="강의 제목 검색..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1); // 검색어 변경 시 1페이지로 초기화
                }}
                className="flex-1"
              />
            </div>

            {/* 강의 목록 */}
            <div className="relative max-h-[400px] min-h-[300px] overflow-y-auto rounded-lg border border-slate-200">
              {isLoading && !data ? (
                <div className="flex h-full min-h-[300px] items-center justify-center">
                  <LoadingSpinner />
                </div>
              ) : data?.data.length === 0 ? (
                <div className="flex h-full min-h-[300px] items-center justify-center text-slate-500">
                  검색 결과가 없습니다.
                </div>
              ) : (
                <div className="relative">
                  {/* 로딩 오버레이 (페이지네이션 시에만 표시, 부드러운 전환) */}
                  {isFetching && data && (
                    <div className="bg-opacity-60 absolute inset-0 z-10 flex items-center justify-center bg-white backdrop-blur-sm transition-opacity duration-200">
                      <LoadingSpinner />
                    </div>
                  )}
                  <ul className="divide-y divide-slate-100">
                    {data?.data.map((course) => {
                      const isSelected = selectedCourseIds.includes(course.id);
                      return (
                        <li
                          key={course.id}
                          className={`flex items-center justify-between p-3 transition-colors hover:bg-slate-50 ${
                            isSelected ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                          } ${isFetching ? 'opacity-60' : ''}`}
                          onClick={() => !isSelected && !isFetching && handleSelect(course)}>
                          <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-slate-900">{course.title}</p>
                              <div className="mt-0.5 flex items-center gap-2">
                                <Badge variant="neutral" size="sm" className="px-1.5 py-0 text-[10px]">
                                  {course.kind}
                                </Badge>
                                <span className="truncate text-xs text-slate-500">
                                  {course.instructor?.name || '강사 미정'}
                                </span>
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <span className="ml-2 flex-shrink-0 text-xs font-medium text-blue-600">선택됨</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            {/* 페이지네이션 (간단 버전) */}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}>
                이전
              </Button>
              <span className="text-sm text-slate-500">
                {page} / {data?.meta.totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= (data?.meta.totalPages || 1)}
                onClick={() => setPage((p) => p + 1)}>
                다음
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseSelectModal;
